import React, {KeyboardEvent, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Box, Chip, TextField} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {SearchContainer} from './SearchWithTagsInput.styled';
import {t} from "i18next";
import FieldHelpSuffix from "./FieldHelpSuffix";

interface SearchWithTagsInputProps {
    onTagsChange: (tags: string[]) => void;
    placeholder?: string;
    initialTags?: string[];
    ariaLabel?: string;
}

const SearchWithTagsInput: React.FC<SearchWithTagsInputProps> = ({
                                                                     onTagsChange,
                                                                     placeholder = (t('searchWithTagsInput.placeholder') as string),
                                                                     initialTags = [],
                                                                     ariaLabel,
                                                                 }) => {
    const [inputValue, setInputValue] = useState('');
    const [tags, setTags] = useState<string[]>(initialTags);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setTags(initialTags);
    }, [initialTags]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    };

    const handleContainerClick = () => {
        inputRef.current?.focus();
    };

    const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && inputValue.trim() !== '') {
            const newTag = inputValue.trim();
            if (!tags.includes(newTag)) {
                const newTags = [...tags, newTag];
                setTags(newTags);
                onTagsChange(newTags);
            }
            setInputValue('');
            event.preventDefault();
        } else if (event.key === 'Backspace' && inputValue === '' && tags.length > 0) {
            handleDeleteTag(tags[tags.length - 1]);
        }
    };

    const handleDeleteTag = (tagToDelete: string) => {
        const newTags = tags.filter(tag => tag !== tagToDelete);
        setTags(newTags);
        onTagsChange(newTags);
    };

    const initialPaddingLeft = 45;
    const [paddingLeft, setPaddingLeft] = useState(initialPaddingLeft);
    const adornmentRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (tags.length === 0) {
            setPaddingLeft(initialPaddingLeft);
            return;
        }

        if (adornmentRef.current) {
            const adornmentWidth = adornmentRef.current.offsetWidth;
            const gapAfterAdornment = 12;
            setPaddingLeft(adornmentWidth + gapAfterAdornment);
        } else {
            setPaddingLeft(initialPaddingLeft);
        }
    }, [tags]);


    return (
        <SearchContainer onClick={handleContainerClick}>
            <TextField
                fullWidth
                variant="standard"
                placeholder={tags.length === 0 ? placeholder : ''}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                inputRef={inputRef}
                inputProps={{
                    role: 'searchbox',
                    'aria-label': ariaLabel ?? (t('searchWithTagsInput.placeholder') as string),
                }}
                InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                        <Box
                            ref={adornmentRef}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                pointerEvents: 'none',
                                zIndex: 1,
                            }}
                        >
                            <SearchIcon sx={{color: 'action.active', marginRight: tags.length > 0 ? '8px' : '0'}}/>
                            {tags.map((tag) => (
                                <Chip
                                    key={tag}
                                    label={tag}
                                    size="small"
                                    onDelete={(e) => {
                                        e.stopPropagation();
                                        handleDeleteTag(tag);
                                    }}
                                    sx={{
                                        margin: '0 4px 0 0',
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        pointerEvents: 'auto',
                                        '& .MuiChip-deleteIcon': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            '&:hover': {
                                                color: 'white',
                                            },
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                    ),
                    endAdornment: (
                        <FieldHelpSuffix
                            asAdornment
                            title={t('searchWithTagsInput.help')}
                            ariaLabel={t('searchWithTagsInput.help') as string}
                        />
                    ),
                }}
                sx={{
                    border: '1px solid rgba(232, 232, 232, 1)',
                    backgroundColor: 'rgb(250,250,250)',
                    borderRadius: '20px',
                    '& .MuiInputBase-root': {
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        paddingRight: '14px',
                        height: '44px',
                        boxSizing: 'border-box',
                    },
                    '& .MuiInputBase-input': {
                        paddingLeft: `${paddingLeft}px !important`,
                        position: 'relative',
                        '&::placeholder': {
                            color: 'rgba(0, 0, 0, 0.42)',
                            opacity: 1,
                        },
                        zIndex: 0,
                        height: '100%',
                        boxSizing: 'border-box',
                    },
                    '& .MuiInput-underline:before, & .MuiInput-underline:after, & .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottom: 'none',
                    },
                }}
            />
        </SearchContainer>
    );
};

export default SearchWithTagsInput;
